import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Wallet, Bell } from "lucide-react";
import elefaMascot from "@/assets/elefa-app-icon.webp";
import { useTranslation } from "react-i18next";
import { useAppApiBalance, formatAudBalance } from "@/hooks/useAppApiBalance";
// useCallerCountry removed: balance is displayed in AUD (source: app API)
// to eliminate FX drift between iOS/Android/web (Option A rewire).
import { useNotifications } from "@/hooks/useNotifications";
import { CurrencySwitcher } from "@/components/connect/CurrencySwitcher";
import { cn } from "@/lib/utils";

// Centralized tab → title mapping. Keep in sync with NativeLargeTitle.
const TAB_TITLE_KEYS: Record<string, string> = {
  "/connect-portal/contacts": "contacts",
  "/connect-portal/dial": "call",
  "/connect-portal/history": "history",
  "/connect-portal/me": "account",
  "/connect-portal/topup": "wallet",
};

/**
 * Phase 4 - Talk360-style persistent balance pill.
 *
 * Left:  "$X.XX + MM:SS min" rounded chip showing both wallet balance AND
 *        the minutes available at the user's chosen target-country mobile
 *        rate. Tappable → routes to the top-up surface.
 *
 * Right: Prominent "+ credits" pill (uses elefa-gold accent) - always-visible
 *        CTA so users can top up from any tab without hunting menus.
 *
 * Center: Telegram-style collapsing inline title that fades in once the
 *         large title scrolls past.
 */
export function ConnectPortalHeader() {
  // Option A rewire: balance pill now sourced from the on-box app API (ASTPP,
  // AUD-native). No client FX — display == billing. Transactions/ledger still
  // come from Supabase elsewhere; this hook is display-only.
  const { balanceAud, isLoading } = useAppApiBalance();
  const { unreadCount } = useNotifications(20);
  const location = useLocation();
  const { t } = useTranslation("common");

  const formattedBalance = formatAudBalance(balanceAud);

  // Title shown inline in the banner - always visible (no scroll-fade).
  // Provides Recents/Contacts/Keypad-style consistency across every tab.
  const title = useMemo(() => {
    const path = location.pathname.replace(/\/$/, "");
    const key = TAB_TITLE_KEYS[path];
    return key ? t(key) : "";
  }, [location.pathname, t]);

  // Visual state: turn the balance pill subtle-red when balance is essentially
  // empty (matches the Talk360 cue), otherwise muted/foreground tones.
  const isLow = balanceAud != null && balanceAud <= 0;

  return (
    <header
      className={cn(
        "sticky top-0 z-40 safe-area-top stable-sticky",
        "border-b border-border",
        "shadow-[0_1px_0_0_hsl(var(--border)),0_6px_18px_-10px_hsl(var(--foreground)/0.22)]",
      )}
      style={{
        isolation: "isolate",
        // Solid surface (no transparency) so scrolling content never bleeds
        // through. Subtle brand wash kept for warmth.
        backgroundImage:
          "linear-gradient(180deg, hsl(var(--primary) / 0.06) 0%, hsl(var(--background)) 60%)",
        backgroundColor: "hsl(var(--background))",
      }}
    >
      <div
        className="flex items-center h-14 px-3 md:px-6 gap-2"
      >
        {/* Left: Balance pill */}
        <Link
          to="/connect-portal/topup"
          aria-label="Wallet balance"
          className={cn(
            "shrink-0 min-h-[38px] flex items-center gap-1.5 rounded-full px-3 text-[14px] font-semibold touchable no-context-menu",
            "bg-muted/60 ring-1 ring-border/40",
            isLow ? "text-destructive" : "text-foreground",
          )}
        >
          <Wallet className="h-4 w-4 opacity-70" />
          {isLoading ? (
            <img
              src={elefaMascot}
              alt=""
              aria-hidden
              draggable={false}
              className="h-4 w-4 object-contain rounded-[22%] animate-elefa-breathe select-none opacity-90"
            />
          ) : (
            <span className="tabular-nums">{formattedBalance}</span>
          )}
        </Link>

        {/* Center: page title - always visible, native iOS inline-title style.
            Matches the Recents/Contacts/Keypad header pattern across the app. */}
        <div className="flex-1 min-w-0 flex items-center justify-center px-1">
          {title && (
            <h1
              className="text-[17px] font-semibold tracking-[-0.01em] text-foreground truncate antialiased"
              style={{ textShadow: "0 1px 1px hsl(var(--background) / 0.6)" }}
            >
              {title}
            </h1>
          )}
        </div>

        {/* Right side: Notifications bell + currency switcher.
            "+ Add credit" intentionally removed - the Wallet bottom-nav tab
            is now the single canonical entry for top-ups (Apple Wallet IA). */}
        <div className="shrink-0 flex items-center gap-1.5">
          <Link
            to="/connect-portal/notifications"
            aria-label={
              unreadCount > 0
                ? `Notifications (${unreadCount} unread)`
                : "Notifications"
            }
            className={cn(
              "relative inline-flex items-center justify-center rounded-full touchable no-context-menu",
              "min-h-[38px] w-[38px] bg-muted/60 ring-1 ring-border/40 text-foreground active:scale-[0.97] transition-transform",
            )}
          >
            <Bell className="h-[18px] w-[18px]" strokeWidth={2.25} />
            {unreadCount > 0 && (
              <span
                aria-hidden="true"
                className={cn(
                  "absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full",
                  "bg-destructive text-destructive-foreground",
                  "text-[10px] font-bold leading-none",
                  "flex items-center justify-center tabular-nums",
                  "ring-2 ring-background",
                )}
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </Link>
          <CurrencySwitcher />
        </div>
      </div>
    </header>
  );
}
